import PropTypes from 'prop-types';
import { addDays } from 'date-fns';
import { useMemo } from 'react';
import { useGetAuthUserQuery } from '@chronos/slices/usersApi';
import {
  initialPermissionsState,
  useGetBillingInfoQuery,
  useGetTrelloBoardAdminSettingsQuery,
  useGetTrelloBoardMemberPermissionsQuery,
  useGetTrelloBoardMemberSettingsQuery,
} from '@chronos/slices/settingsApi';
import {
  initialBoardsState,
  useGetBoardsQuery,
} from '@chronos/slices/boardsApi';
import { BoardContext } from '@chronos/ui-toolkit/contexts';
import Spinner from '@chronos/components/Spinner';
import { useGetTrelloBoardCardsQuery } from '@chronos/slices/cardsApi';
import { trelloCardFields } from '../constants';

export const BoardContextWrapper = ({
  children,
  boardId,
  organizationId,
  isInitialized,
}) => {
  const belongToOrganization = true;
  const { data: profile } = useGetAuthUserQuery(null);
  const { data: { entities: boardsEntities } = initialBoardsState } =
    useGetBoardsQuery({ memberId: profile.id });
  const currentBoard = boardsEntities[boardId];

  const {
    data: trelloBoardMemberSettings,
    isFetching: trelloBoardMemberSettingsFetching,
    isSuccess: trelloBoardMemberSettingsRequestSuccess,
  } = useGetTrelloBoardMemberSettingsQuery(
    {
      boardId,
    },
    { skip: !isInitialized }
  );

  const {
    data: trelloBoardAdminSettings,
    isFetching: trelloBoardAdminSettingsFetching,
    isSuccess: trelloBoardAdminSettingsRequestSuccess,
  } = useGetTrelloBoardAdminSettingsQuery(
    {
      boardId,
    },
    { skip: !isInitialized }
  );

  const {
    data: trelloBoardMemberPermissions = initialPermissionsState,
    isFetching: trelloBoardMemberPermissionsFetching,
    isSuccess: trelloBoardMemberPermissionsRequestSuccess,
  } = useGetTrelloBoardMemberPermissionsQuery(
    {
      boardId,
    },
    { skip: !isInitialized }
  );

  const {
    data: billingInfo,
    isFetching: billingInfoFetching,
    isSuccess: billingInfoRequestSuccess,
  } = useGetBillingInfoQuery(
    {
      organizationId,
    },
    { skip: !isInitialized }
  );
  const { isFetching: cardsFetching, isSuccess: cardsRequestSuccess } =
    useGetTrelloBoardCardsQuery({
      boardId,
      filter: 'visible',
      fields: trelloCardFields,
    });

  const isBoardLoaded =
    !trelloBoardMemberSettingsFetching &&
    trelloBoardMemberSettingsRequestSuccess &&
    !trelloBoardAdminSettingsFetching &&
    trelloBoardAdminSettingsRequestSuccess &&
    !trelloBoardMemberPermissionsFetching &&
    trelloBoardMemberPermissionsRequestSuccess &&
    !billingInfoFetching &&
    billingInfoRequestSuccess &&
    !cardsFetching &&
    cardsRequestSuccess &&
    isInitialized;

  const boardContext = useMemo(() => {
    const billingRelatedContext = {};
    if (billingInfo) {
      const now = new Date();
      const finished = new Date(billingInfo.finishedAt);
      const pastLogsAvailable = addDays(finished, 5);
      billingRelatedContext.isLicenseExpired = billingInfo.freeMode
        ? false
        : now >= finished;
      billingRelatedContext.pastLogsAvailable = billingInfo.freeMode
        ? true
        : pastLogsAvailable > now;
    }
    return {
      memberId: profile.id,
      boardId,
      board: currentBoard,
      organizationId,
      boardMemberSettings: trelloBoardMemberSettings,
      boardMemberPermissions:
        trelloBoardMemberPermissions.entities[
          trelloBoardMemberPermissions.ids[0]
        ],
      boardAdminSettings: trelloBoardAdminSettings,
      belongToOrganization,
      profile,
      // chronosJWT,
      // trelloToken,
      ...billingRelatedContext,
    };
  }, [
    billingInfo,
    profile,
    boardId,
    currentBoard,
    organizationId,
    trelloBoardMemberSettings,
    trelloBoardAdminSettings,
    trelloBoardMemberPermissions,
  ]);

  if (!isBoardLoaded) {
    return (
      <Spinner
        size="xlarge"
        style={{ height: '100vh' }}
      />
    );
  }

  return (
    <BoardContext.Provider value={boardContext}>
      {children}
    </BoardContext.Provider>
  );
};

BoardContextWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  boardId: PropTypes.string.isRequired,
  organizationId: PropTypes.string.isRequired,
  isInitialized: PropTypes.bool.isRequired,
};
